import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import client from '../../api/client';

const STATUS_COLOR = {
  Open: 'bg-blue-50 text-blue-700 border-blue-200',
  Logged: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Troubleshooting: 'bg-amber-50 text-amber-700 border-amber-200',
  Resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Closed: 'bg-gray-100 text-gray-500 border-gray-200',
};

const STATUS_DOT = {
  Open: 'bg-blue-500',
  Logged: 'bg-indigo-500',
  Troubleshooting: 'bg-amber-500',
  Resolved: 'bg-emerald-500',
  Closed: 'bg-gray-400',
};

export default function BHELTickets() {
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const isManagement = user.role === 'BHEL_MANAGEMENT';
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [locations, setLocations] = useState([]);
  const [locFilter, setLocFilter] = useState('');
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (locFilter) params.location = locFilter;
    client.get('/bhel/tickets/', { params }).then(r => setTickets(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [statusFilter, locFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isManagement) client.get('/locations/').then(r => setLocations(r.data?.results || r.data || [])).catch(() => {});
  }, [isManagement]);

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold text-[#C1278D] uppercase tracking-widest mb-1">Support</p>
          <h1 className="text-3xl font-bold text-gray-900">Tickets</h1>
          {!loading && (
            <p className="text-gray-500 text-sm mt-1">{tickets.length} ticket{tickets.length !== 1 ? 's' : ''} found</p>
          )}
        </div>
        {!isManagement && (
          <button
            onClick={() => navigate('/bhel/tickets/new')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold shadow-sm transition-all hover:shadow-md hover:scale-[1.02] active:scale-100"
            style={{ background: 'linear-gradient(135deg, #C1278D, #3D3562)' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4"/></svg>
            Raise Ticket
          </button>
        )}
      </div>

      {/* Filter Bar */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl bg-white text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-[#3D3562]/30 focus:border-[#3D3562] shadow-sm appearance-none cursor-pointer"
          >
            <option value="">All Statuses</option>
            {['Open','Logged','Entitlement Confirmed','Ticket Assignment','Troubleshooting','On Hold','Resolved','Closed'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        {isManagement && (
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            <select
              value={locFilter}
              onChange={e => setLocFilter(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl bg-white text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-[#3D3562]/30 focus:border-[#3D3562] shadow-sm appearance-none cursor-pointer"
            >
              <option value="">All Locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        )}
        {(statusFilter || locFilter) && (
          <button
            onClick={() => { setStatusFilter(''); setLocFilter(''); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-500 border border-gray-200 rounded-xl bg-white hover:bg-gray-50 transition-colors shadow-sm"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#C1278D] border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">Loading tickets…</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
            </div>
            <p className="text-gray-500 font-semibold">No tickets found</p>
            <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-[#3D3562] to-[#272244]">
                <th className="px-5 py-3.5 text-left text-xs font-bold text-purple-200 uppercase tracking-wider">Ticket #</th>
                <th className="px-5 py-3.5 text-left text-xs font-bold text-purple-200 uppercase tracking-wider">Subject</th>
                <th className="px-5 py-3.5 text-left text-xs font-bold text-purple-200 uppercase tracking-wider">Location</th>
                <th className="px-5 py-3.5 text-left text-xs font-bold text-purple-200 uppercase tracking-wider">Room</th>
                <th className="px-5 py-3.5 text-left text-xs font-bold text-purple-200 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3.5 text-left text-xs font-bold text-purple-200 uppercase tracking-wider">Raised On</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t, idx) => (
                <tr
                  key={t.id}
                  className={`cursor-pointer transition-all duration-150 hover:bg-purple-50 group ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                  onClick={() => navigate(`/bhel/tickets/${t.id}`)}
                >
                  <td className="px-5 py-3.5">
                    <span className="font-mono text-xs font-bold text-[#3D3562] bg-purple-50 px-2 py-1 rounded-lg border border-purple-100">{t.ticket_number}</span>
                  </td>
                  <td className="px-5 py-3.5 font-semibold text-gray-800 group-hover:text-[#3D3562] transition-colors max-w-xs truncate">{t.subject}</td>
                  <td className="px-5 py-3.5 text-gray-500">{t.location_name}</td>
                  <td className="px-5 py-3.5 text-gray-500">{t.room_name}</td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${STATUS_COLOR[t.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[t.status] || 'bg-gray-400'}`} />
                      {t.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-gray-400 text-xs">{new Date(t.fault_reported_at).toLocaleDateString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
